import { CLIENT_ERROR_CATEGORY } from "@/loggers/types";

/* -------------------------------------------------------------------------- */
/* Message / type extraction                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Normalises any thrown value (Error, string, rejection reason, object) into a
 * single human-readable message string for the `client_error` event.
 */
export const toErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message || error.name || "unknown error";
  }

  if (typeof error === "string") {
    return error || "unknown error";
  }

  if (error && typeof error === "object") {
    const maybeMessage = (error as { message?: unknown }).message;
    if (typeof maybeMessage === "string" && maybeMessage) {
      return maybeMessage;
    }
    try {
      return JSON.stringify(error);
    } catch {
      return "unknown error";
    }
  }

  if (
    typeof error === "number" ||
    typeof error === "boolean" ||
    typeof error === "bigint" ||
    typeof error === "symbol"
  ) {
    return error.toString();
  }

  return "unknown error";
};

/**
 * Extracts the error's constructor name (TypeError, ReferenceError, …) so the
 * dashboard can break uncaught errors down by JS error type. Falls back to a
 * generic "Error" for non-Error throwables.
 */
export const toErrorName = (error: unknown): string => {
  if (error instanceof Error) {
    return error.name || "Error";
  }

  if (error && typeof error === "object") {
    const maybeName = (error as { name?: unknown }).name;
    if (typeof maybeName === "string" && maybeName) {
      return maybeName;
    }
  }

  return "Error";
};

/**
 * Extracts the error's stack trace so the dashboard can show where an error
 * originated (file/function/line). Only genuine Error instances carry a stack;
 * resource failures and non-Error throwables return "".
 */
export const toErrorStack = (error: unknown): string => {
  if (error instanceof Error && typeof error.stack === "string") {
    return error.stack;
  }

  return "";
};

/* -------------------------------------------------------------------------- */
/* Categorisation                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Patterns emitted by browsers / bundlers when a JS or CSS chunk fails to load
 * (typically after a deploy invalidates previously-hashed chunk URLs).
 */
const CHUNK_ERROR_PATTERN =
  /ChunkLoadError|Loading chunk [\w-]+ failed|Loading CSS chunk|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;

export const isChunkLoadError = (error: unknown): boolean => {
  if (error instanceof Error && error.name === "ChunkLoadError") {
    return true;
  }
  return CHUNK_ERROR_PATTERN.test(toErrorMessage(error));
};

/**
 * Resolves the category for an error. Chunk-load failures are detected first so
 * they are never miscounted as a generic uncaught error or promise rejection;
 * everything else falls back to the category of the source that reported it.
 */
export const categorizeError = (error: unknown, fallback: CLIENT_ERROR_CATEGORY): CLIENT_ERROR_CATEGORY =>
  isChunkLoadError(error) ? CLIENT_ERROR_CATEGORY.CHUNK_LOAD : fallback;

/* -------------------------------------------------------------------------- */
/* Dedupe / rate-limit                                                        */
/* -------------------------------------------------------------------------- */

const DEFAULT_WINDOW_MS = 10_000;
const MAX_ENTRIES = 500;

const lastSeen = new Map<string, number>();

const prune = (now: number, windowMs: number) => {
  for (const [signature, timestamp] of lastSeen) {
    if (now - timestamp >= windowMs) {
      lastSeen.delete(signature);
    }
  }
};

/**
 * Rate-limits identical errors so a render loop cannot flood the RUM/Loki
 * pipeline. Returns true the first time a signature is seen (and again only
 * after `windowMs` has elapsed), false for repeats within the window.
 *
 * `now` is injectable for deterministic testing.
 */
export const shouldEmit = (signature: string, windowMs = DEFAULT_WINDOW_MS, now = Date.now()): boolean => {
  const previous = lastSeen.get(signature);
  if (previous !== undefined && now - previous < windowMs) {
    return false;
  }

  if (lastSeen.size >= MAX_ENTRIES) {
    prune(now, windowMs);
  }

  lastSeen.set(signature, now);
  return true;
};

/** Test-only helper to clear the dedupe cache between cases. */
export const resetDedupeCache = (): void => {
  lastSeen.clear();
};
